import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import {
  GetFilterUserAppointmentGroupsOptionsQuery,
  useGetFilterUserAppointmentGroupsOptionsLazyQuery,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums';

interface FilterAppointmentsMenuProps {
  selectedAppointmentGroupingIds: string[];
  clinicId?: string;
  userId?: string;
  setGroupPage: (page: number) => void;
  setSelectedAppointmentGroupingIds: Dispatch<SetStateAction<string[]>>;
}

export type AppointmentGroupingOption = NonNullable<
  GetFilterUserAppointmentGroupsOptionsQuery['userAppointmentGroupings']
>[0];

const FilterAppointmentsMenu = ({
  selectedAppointmentGroupingIds,
  setSelectedAppointmentGroupingIds,
  clinicId,
  setGroupPage,
  userId,
}: FilterAppointmentsMenuProps): JSX.Element => {
  const [getFilterMenuData, { data: filterMenuData, loading: isLoading }] =
    useGetFilterUserAppointmentGroupsOptionsLazyQuery({
      variables: {
        where: {
          clinicId: { equals: clinicId },
          userId: { equals: userId },
        },
      },
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

  const toggleAppointmentGroupingFilter = useCallback(
    (value) => {
      const selectedIds = value.map((item: MenuItemProps) => {
        if (item && typeof item.value === 'string') return item.value;
        return;
      });
      setSelectedAppointmentGroupingIds(selectedIds);
      // Reset pagination back to first page
      setGroupPage(1);
    },
    [setSelectedAppointmentGroupingIds, setGroupPage],
  );

  const appointmentGroupOptions: MenuItemProps[] = useMemo(
    () =>
      filterMenuData?.userAppointmentGroupings?.map((filter: AppointmentGroupingOption): MenuItemProps => {
        const isSelected = selectedAppointmentGroupingIds?.includes(filter.id);
        return {
          label: filter.label,
          value: filter.id,
          isSelected,
        };
      }) || [],
    [filterMenuData?.userAppointmentGroupings, selectedAppointmentGroupingIds],
  );

  const isFiltering = useMemo(() => !!selectedAppointmentGroupingIds.length, [selectedAppointmentGroupingIds]);

  return (
    <Menu
      containerProps={{
        onOpen: getFilterMenuData,
        onClose: (value): void => toggleAppointmentGroupingFilter(value),
      }}
      buttonProps={{
        variant: isFiltering ? 'ghost' : 'secondarySubtle',
        ...(isFiltering && { bg: 'background.secondaryAlpha.hover' }),
        size: 'sm',
        text: 'Filter Columns',
        leftIconName: 'filter',
        showRightIcon: false,
      }}
      listProps={{
        isGrouped: true,
        isMultiSelect: true,
        isSearchable: true,
        menuItems: appointmentGroupOptions,
        isLoading: isLoading,
      }}
    />
  );
};

export default FilterAppointmentsMenu;
